import * as React from 'react';
import styled from 'styled-components';
import { LeftContentContainerStyles } from '../../../styling/styles';
import BREAK_POINTS, {
  BREAK_POINT_WIDTH_TABLET_BIG,
  BREAK_POINT_WIDTH_TABLET,
  BREAK_POINT_WIDTH_MOBILE_BIG,
  BREAK_POINT_WIDTH_DESKTOP,
} from 'packages/smart-home-support/styling/media-queries';
import SectionFrame, { SectionFrameProps } from './SectionFrame';
import { Heading2, BodyText } from 'packages/smart-home-support/components/Typography';
import { lifecycle, withStateHandlers, compose } from 'recompose';

const iconAssets = [
  require('../resources/SHSProdIcons__TV_with_Remote.svg'),
  require('../resources/SHSProdIcons_Alexa.svg'),
  require('../resources/SHSProdIcons_Game.svg'),
  require('../resources/SHSProdIcons_Printer.svg'),
  require('../resources/SHSProdIcons_DVD_Player.svg'),
  require('../resources/SHSProdIcons_Smart_Lights.svg'),
  require('../resources/SHSProdIcons_Nest_Therm.svg'),
  require('../resources/SHSProdIcons_Nest_Cam.svg'),
  require('../resources/SHSProdIcons_Laptop.svg'),
  require('../resources/SHSProdIcons_Ring_Doorbell.svg'),
  require('../resources/SHSProdIcons_Speakers.svg'),
  require('../resources/SHSProdIcons_Mouse.svg'),
  require('../resources/SHSProdIcons_Desktop_Computer.svg'),
  require('../resources/SHSProdIcons_Smartwatch.svg'),
  require('../resources/SHSProdIcons_Smart_Lock.svg'),
  require('../resources/SHSProdIcons_Router.svg'),
  require('../resources/SHSProdIcons_Echo_Dot.svg'),
  require('../resources/SHSProdIcons_Tablet.svg'),
  require('../resources/SHSProdIcons_Headphones.svg'),
  require('../resources/SHSProdIcons_Google_Home.svg'),
  require('../resources/SHSProdIcons_Smart_Alarm.svg'),
  require('../resources/SHSProdIcons__Fitness_Watch.svg'),
  require('../resources/SHSProdIcons_Monitor.svg'),
  require('../resources/SHSProdIcons_Security_Camera.svg'),
  require('../resources/SHSProdIcons_Kindle.svg'),
  require('../resources/SHSProdIcons_Keyboard.svg'),
  require('../resources/SHSProdIcons__Chomecast.svg'),
];

const SectionContainer = styled(SectionFrame)`
  background-color: ${(props) => props.theme.colors.featureSection1};

  @media ${BREAK_POINTS.tablet} {
    display: flex;
    flex-direction: row;
  }
`;

const ContentFrame = styled.div`
  ${LeftContentContainerStyles}

  display: flex;
  flex-direction: column;
  justify-content: center;

  @media ${BREAK_POINTS.desktop} {
    max-width: 700px;
  }
`;

const ContentContainer = styled.div`
  flex: 5;
  display: flex;
  justify-content: flex-start;
`;

const IconContainer = styled.div`
  flex: 4;
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 15px;

  @media ${BREAK_POINTS.desktop} {
    padding: 30px;
  }
`;

const IconImageFrame = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;

  width: 100%;

  height: auto;

  @media ${BREAK_POINTS.tablet} {
    height: 100%;
  }
`;

const Heading = styled(Heading2)``;

const Body = styled(BodyText)`
  margin-top: 25px;
`;

function getIconContainerWidth() {
  const iconsBasis = 5;
  const contentBasis = 4;
  const spacing = 40;
  const mobileSpacing = spacing + 30;
  const iconsToContentRatio = iconsBasis / (iconsBasis + contentBasis);
  if (window.innerWidth >= BREAK_POINT_WIDTH_TABLET) {
    return iconsToContentRatio * window.innerWidth - spacing;
  }

  return window.innerWidth - mobileSpacing;
}

function getIconContainerHeight() {
  const container = document.getElementById('iconContainer');

  if (container && container.getBoundingClientRect) {
    const height = container.getBoundingClientRect().height;
    return height;
  }

  return 400;
}

export const withWindowState = withStateHandlers(
  {
    width: getIconContainerWidth(),
    height: getIconContainerHeight(),
  },
  {
    updateDimensions: () => () => ({
      width: getIconContainerWidth(),
      height: getIconContainerHeight(),
    }),
  }
);

export const withWindowResizeListeners = lifecycle<any, any, any>({
  componentDidMount() {
    this.props.updateDimensions();
    window.addEventListener('resize', this.props.updateDimensions);
  },
  componentWillUnmount() {
    window.removeEventListener('resize', this.props.updateDimensions);
  },
});

interface IconGridProps extends React.HTMLProps<HTMLDivElement> {
  width: number;
  height: number;
}

const GridIcon = styled.img`
  height: ${(props: { size: number }) => props.size}px;
  width: ${(props) => props.size}px;
  padding: 10px;
`;

const IconRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  align-self: stretch;
`;

const getPaddedIconSize = (iconSize: number, paddingFactor: number) => iconSize + Math.ceil(iconSize * paddingFactor);

const getIconSizesByBreakpoint = () => {
  let iconSize: number;
  let paddedIconSize: number;
  const width = window.innerWidth;

  iconSize = 60;
  paddedIconSize = getPaddedIconSize(iconSize, 0.2);

  if (width >= BREAK_POINT_WIDTH_MOBILE_BIG) {
    iconSize = 75;
    paddedIconSize = getPaddedIconSize(iconSize, 0.3);
  }

  if (width >= BREAK_POINT_WIDTH_TABLET_BIG) {
    iconSize = 85;
    paddedIconSize = getPaddedIconSize(iconSize, 0.3);
  }

  if (width >= BREAK_POINT_WIDTH_DESKTOP) {
    iconSize = 95;
    paddedIconSize = getPaddedIconSize(iconSize, 0.4);
  }

  return { iconSize, paddedIconSize };
};

const IconGrid: React.FunctionComponent = (props: IconGridProps) => {
  const { height, width } = props;
  const { iconSize, paddedIconSize } = getIconSizesByBreakpoint();
  const isMobileBig = window.innerWidth >= BREAK_POINT_WIDTH_MOBILE_BIG && window.innerWidth < BREAK_POINT_WIDTH_TABLET;

  let rows = 4;
  if (window.innerWidth >= BREAK_POINT_WIDTH_TABLET) rows = 5;
  if (window.innerWidth >= BREAK_POINT_WIDTH_DESKTOP) rows = Math.floor(height / paddedIconSize);

  let cols = Math.floor(width / paddedIconSize);
  if (isMobileBig && cols > 5) cols = 5;

  let index = 0;

  const gridIcons = Array(rows)
    .fill(1)
    .map((_, i) => {
      const svgs = Array(cols)
        .fill(1)
        .map(() => iconAssets[index++ % iconAssets.length]);

      const icons = svgs.map((svg, k) => <GridIcon key={k} src={svg} size={iconSize} />);

      return <IconRow key={i}>{icons}</IconRow>;
    });

  return (
    <IconContainer id="iconContainer">
      <IconImageFrame>{gridIcons}</IconImageFrame>
    </IconContainer>
  );
};

const ResponsiveIconGrid = compose(
  withWindowState,
  withWindowResizeListeners
)(IconGrid);

const WhatsCovered: React.SFC = () => {
  const margins = {
    bottom: true,
  };

  const frameProps: SectionFrameProps = {
    mobile: margins,
    desktop: margins,
  };

  return (
    <SectionContainer {...frameProps}>
      <ContentContainer>
        <ContentFrame>
          <Heading>Your home is full of tech. Is it protected?</Heading>
          <Body>
            Smart Home Support protects the tech in your home under one simple plan. Your old tech, brand new tech and
            future tech purchases are automatically covered when you sign up.
          </Body>
        </ContentFrame>
      </ContentContainer>
      <ResponsiveIconGrid />
    </SectionContainer>
  );
};

export default WhatsCovered;



// WEBPACK FOOTER //
// ./packages/smart-home-support/pages/homepage/components/WhatsCovered.tsx